import { useState } from "react";
import {
  customFieldClassName,
  customFieldLabel,
  fieldsForLocation,
  fetchCustomValues,
  saveCustomValues,
  type CustomValues,
} from "@/data/customFieldsRepository";
import { useAsync } from "@/components/useAsync";
import { useApp, useUiDispatch } from "@/app/AppContext";
import { StatusMessage, type Message } from "@/components/StatusMessage";
import {
  saveIntervention,
  saveAdminQuestionnaireOnly,
  saveAdminDisplayInformation,
  saveWorkingComment,
  setInterventionStatus,
  softDeleteIntervention,
  type LocationQuestionnaire,
} from "@/data/interventionsRepository";
import {
  createPublicDraft,
  getClassificationViews,
  validateInterventionDraft,
  type PublicDraft,
} from "@/domain/interventionForm";
import {
  canApproveIntervention,
  canClassify,
  canDeleteIntervention,
  canEditIntervention,
  canReturnToDraft,
  canSubmitOwn,
  isRespondentOnly,
  isSuperadmin,
} from "@/domain/permissions";
import {
  findQuestionnaire,
  getPublicValues,
  type AnswerValue,
  type Answers,
  type QuestionnaireDefinition,
  type QuestionnaireResponse,
} from "@/domain/questionnaire";
import { sortArenas } from "@/domain/settings";
import type { Intervention } from "@/domain/types";
import { InterventionTabs } from "@/features/details/InterventionTabs";
import { UpdatedMeta } from "@/features/details/UpdatedMeta";
import { QuestionnaireForm } from "@/features/questionnaire/QuestionnaireForm";
import { useI18n } from "@/i18n/useI18n";
import { WORKING_COMMENT_COLUMN } from "@/domain/workingComments";
import { returnToDraftWithFeedback, type InterventionFeedback } from "@/data/interventionFeedbackRepository";
import { InterventionFeedbackPanel } from "./InterventionFeedbackPanel";
import { useEditorSources } from "./useEditorSources";

/** Public draft keys that a questionnaire `publicMapping` may target. */
const PUBLIC_TARGETS: Record<string, keyof PublicDraft> = {
  Title_NO: "titleNo",
  Title_EN: "titleEn",
  Description_NO: "descriptionNo",
  Description_EN: "descriptionEn",
  Objectives: "objectives",
  Youngest: "youngest",
  Oldest: "oldest",
  links: "links",
};

interface InterventionEditorProps {
  /** `null` creates a new intervention. */
  item: Intervention | null;
}

export function InterventionEditor({ item }: InterventionEditorProps) {
  const { t } = useI18n();
  const { user, data } = useApp();
  const dispatch = useUiDispatch();
  const sources = useEditorSources(
    item?.dbId ?? null,
    canClassify(user),
    data.settings.experimentalFeatures.respondentReturnFeedback,
  );

  const close = () => {
    if (isRespondentOnly(user)) {
      dispatch({ type: "closePanel" });
      dispatch({ type: "setTab", tab: "list" });
    } else if (item) dispatch({ type: "showDetails", itemId: item.id });
    else dispatch({ type: "closePanel" });
  };

  if (item && !canEditIntervention(user, item)) return null;
  if (sources.status === "error") {
    return (
      <>
        <p className="auth-gate-message error">{`${t("formLoadError")} ${sources.error.message}`}</p>
        <button type="button" onClick={close}>
          {t("closeDetails")}
        </button>
      </>
    );
  }
  if (sources.status !== "ready") return <div aria-busy="true">…</div>;
  return (
    <EditorForm
      item={item}
      locations={sources.value.locations}
      response={sources.value.response}
      comment={sources.value.comment}
      feedback={sources.value.feedback}
      onClose={close}
    />
  );
}

interface EditorFormProps {
  item: Intervention | null;
  locations: LocationQuestionnaire[];
  response: QuestionnaireResponse | null;
  comment: string;
  feedback: InterventionFeedback[];
  onClose(): void;
}

function EditorForm({
  item,
  locations,
  response,
  comment: savedComment,
  feedback,
  onClose,
}: EditorFormProps) {
  const i18n = useI18n();
  const { t } = i18n;
  const { authMode, user, data } = useApp();
  const dispatch = useUiDispatch();
  const { views, arenas, customFields } = data.settings;
  const feedbackEnabled = data.settings.experimentalFeatures.respondentReturnFeedback;
  const isCreate = item === null;
  const isRespondent = canSubmitOwn(user);
  const locationLocked = !isSuperadmin(user);
  // New interventions start in the user's own location, or in the only location there is.
  const defaultRegion = !isCreate
    ? ""
    : locationLocked
      ? (user?.location ?? "")
      : locations.length === 1
        ? locations[0]!.name
        : "";

  const [draft, setDraft] = useState<PublicDraft>(() =>
    createPublicDraft(item, views, defaultRegion),
  );
  const editableCustomFields = canClassify(user) ? fieldsForLocation(customFields, draft.region) : [];
  const customSource = useAsync(
    item?.dbId && canClassify(user) ? item.dbId : null,
    () => fetchCustomValues(item!.dbId!),
  )[0];
  const [customValues, setCustomValues] = useState<CustomValues>({});
  const [customInitialised, setCustomInitialised] = useState(false);
  if (!customInitialised && customSource.status === "ready") {
    setCustomValues(customSource.value);
    setCustomInitialised(true);
  }
  const [answers, setAnswers] = useState<Answers>(() => ({ ...(response?.answers ?? {}) }));
  // null = leave the prior title-source preference unchanged; true = manual
  // title in Visningsinformasjon; false = return to questionnaire-driven title.
  const [titleOverride, setTitleOverride] = useState<boolean | null>(null);
  // Track the editor tab so saving questionnaire answers never validates or
  // overwrites fields from the separate Visningsinformasjon tab.
  const [activeSection, setActiveSection] = useState<"public" | "private">(
    isRespondent ? "private" : "public",
  );
  // Older imports left the comment in row data; offer it so saving moves it.
  const [comment, setComment] = useState(
    () => savedComment || String(item?.sourceRow[WORKING_COMMENT_COLUMN] ?? "").trim(),
  );
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);
  const [copyDone, setCopyDone] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [returnComment, setReturnComment] = useState("");

  // An intervention keeps the questionnaire it was created with (recorded on its
  // response and on the intervention); only new or older unpinned ones follow the location.
  const pinned = response
    ? { id: response.questionnaire_id, version: response.questionnaire_version }
    : (item?.questionnaire ?? null);
  const location = locations.find((entry) => entry.name === draft.region);
  const config: QuestionnaireDefinition | null = pinned
    ? (findQuestionnaire(pinned.id, pinned.version) ?? null)
    : location
      ? (findQuestionnaire(location.questionnaire_id, location.questionnaire_version) ?? null)
      : null;

  const setField = <K extends keyof PublicDraft>(key: K, value: PublicDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const setAnswer = (key: string, value: AnswerValue) =>
    setAnswers((current) => ({ ...current, [key]: value }));
  const fail = (text: string) => setMessage({ kind: "error", text });

  const afterChange = async (next: "list" | "reopen") => {
    const refreshed = await data.reload();
    if (!refreshed) return fail(t("editRefreshError"));
    if (next === "reopen" && item)
      dispatch({ type: "editIntervention", itemId: item.id, editOnly: true });
    else {
      dispatch({ type: "closePanel" });
      dispatch({ type: "setTab", tab: "list" });
    }
  };

  const save = async (requestedStatus: "draft" | "submitted" | null) => {
    if (!isCreate && !item?.dbId) return fail(t("formLoadError"));
    const savingQuestionnaire = isRespondent || activeSection === "private";
    const result = validateInterventionDraft(draft, answers, {
      isCreate,
      saveSection: savingQuestionnaire ? "questionnaire" : "display",
      isRespondent,
      includeClassifications: canClassify(user),
      requestedStatus,
      registeredArenas: (arenas ?? []).map((arena) => arena.key),
      item,
      config,
      existingAnswers: response?.answers ?? {},
      hasExistingResponse: !!response,
      views,
    });
    if (!result.ok) return fail(t(result.error));
    setBusy(true);
    try {
      const interventionId =
        savingQuestionnaire && !isRespondent
          ? await saveAdminQuestionnaireOnly({
              interventionId: item?.dbId ?? null,
              location: draft.region.trim(),
              questionnaireId: result.config.id,
              questionnaireVersion: result.config.version,
              answers: result.answers ?? {},
            })
          : isRespondent
            ? await saveIntervention({
                interventionId: item?.dbId ?? null,
                location: draft.region.trim(),
                rowData: result.rowData,
                questionnaireId: result.config.id,
                questionnaireVersion: result.config.version,
                answers: result.answers,
                status: requestedStatus,
              })
            : await saveAdminDisplayInformation({
                interventionId: item?.dbId ?? null,
                location: draft.region.trim(),
                rowData: result.rowData,
                questionnaireId: result.config.id,
                questionnaireVersion: result.config.version,
                titleOverride,
              });
      if (!savingQuestionnaire && canClassify(user)) {
        await saveCustomValues(
          interventionId,
          fieldsForLocation(customFields, draft.region),
          customValues,
        );
        if (comment.trim() !== savedComment.trim()) {
          await saveWorkingComment(interventionId, comment);
        }
      }
      await afterChange("list");
    } catch (error) {
      fail(`${t("editSaveError")} (${(error as Error).message})`);
    } finally {
      setBusy(false);
    }
  };

  const runAction = async (
    action: () => Promise<void>,
    errorKey: Parameters<typeof t>[0],
    next: "list" | "reopen",
  ) => {
    setBusy(true);
    try {
      await action();
      await afterChange(next);
    } catch (error) {
      fail(`${t(errorKey)} (${(error as Error).message})`);
    } finally {
      setBusy(false);
    }
  };

  const copyFromQuestionnaire = () => {
    if (!config?.publicMapping?.length || !window.confirm(t("formCopyConfirm"))) return;
    const values = getPublicValues(config, answers, i18n.language);
    const classificationColumns = new Set(getClassificationViews(views).map((view) => view.column));
    const viewColumns = new Set(views.map((view) => view.column));
    const unknown = Object.keys(values).find(
      (key) => !PUBLIC_TARGETS[key] && !viewColumns.has(key),
    );
    if (unknown)
      return fail(`${t("formLoadError")} Public mapping target is unavailable: ${unknown}`);
    // Copying a questionnaire title explicitly restores automatic title sync.
    if ("Title_NO" in values || "Title_EN" in values) setTitleOverride(false);
    setDraft((current) => {
      const next = { ...current, classifications: { ...current.classifications } };
      for (const [key, value] of Object.entries(values)) {
        const target = PUBLIC_TARGETS[key];
        if (target) (next[target] as string) = value;
        // Fields of hidden views are not in the form, so their values are not copied.
        else if (classificationColumns.has(key)) next.classifications[key] = value;
      }
      return next;
    });
    setCopyDone(true);
  };

  const sortedArenas = sortArenas(arenas ?? []);
  const arenaRegistered = sortedArenas.some((arena) => arena.key === draft.arena);
  const statusLabel =
    item?.status === "draft"
      ? t("statusDraft")
      : item?.status === "approved"
        ? t("statusApproved")
        : t("statusSubmitted");

  // Respondents only fill in the questionnaire; the arena is set by an admin.
  const hidePublicTab = authMode === "individual" && isRespondentOnly(user);
  const arenaField = (
    <div className="form-field">
      <label htmlFor="form-arena">{t("arena")}</label>
      <select
        id="form-arena"
        disabled={isRespondent}
        value={arenaRegistered ? draft.arena : ""}
        onChange={(event) => setField("arena", event.target.value)}
      >
        <option value="">
          {t("formArenaPlaceholder")}
        </option>
        {sortedArenas.map((arena) => (
          <option key={arena.key} value={arena.key}>
            {i18n.arenaLabel(arena.key)}
          </option>
        ))}
      </select>
    </div>
  );
  const locationField = (
    <div className="form-field">
      <label htmlFor="form-region">{t("location")}</label>
      {locationLocked ? (
        <input type="text" id="form-region" value={draft.region} readOnly />
      ) : (
        <select
          id="form-region"
          value={draft.region}
          onChange={(event) => setField("region", event.target.value)}
        >
          <option value="">{t("formChooseLocation")}</option>
          {locations.map((location) => (
            <option key={location.name} value={location.name}>
              {location.name}
            </option>
          ))}
        </select>
      )}
    </div>
  );

  // One title and one description field. They edit the Norwegian text and clear the
  // English one, which then falls back to it; an untouched English text is kept.
  const publicFields = (
    <>
      <fieldset disabled={isRespondent} style={{ border: 0, padding: 0, margin: 0 }}>
        <TextField
          id="form-title"
          label={t("formTitle")}
          value={draft.titleNo || draft.titleEn}
          onChange={(value) => {
            setTitleOverride(true);
            setDraft((current) => ({ ...current, titleNo: value, titleEn: "" }));
          }}
        />
        <TextField
          id="form-description"
          label={t("formDescription")}
          value={draft.descriptionNo || draft.descriptionEn}
          multiline
          onChange={(value) =>
            setDraft((current) => ({ ...current, descriptionNo: value, descriptionEn: "" }))
          }
        />
      </fieldset>
      {arenaField}
      <fieldset disabled={isRespondent} style={{ border: 0, padding: 0, margin: 0 }}>
        <div className="form-row">
          <TextField
            id="form-youngest"
            type="number"
            label={t("formYoungest")}
            required
            value={draft.youngest}
            onChange={(value) => setField("youngest", value)}
          />
          <TextField
            id="form-oldest"
            type="number"
            label={t("formOldest")}
            required
            value={draft.oldest}
            onChange={(value) => setField("oldest", value)}
          />
        </div>
        <TextField
          id="form-links"
          label={t("formLinksLabel")}
          value={draft.links}
          multiline
          onChange={(value) => setField("links", value)}
        />
        <TextField
          id="form-objectives"
          label={t("objective")}
          value={draft.objectives}
          multiline
          onChange={(value) => setField("objectives", value)}
        />
      </fieldset>
      {!isRespondent && (
        <>
          <button
            type="button"
            id="copy-questionnaire-description"
            className="details-save-btn"
            disabled={!response || !config?.publicMapping?.length}
            onClick={copyFromQuestionnaire}
          >
            {t("formFetchDisplayInformation")}
          </button>
          <p role="status">{copyDone ? t("formCopyDone") : ""}</p>
        </>
      )}
        {canClassify(user) && editableCustomFields.length > 0 && (
          <section className="intervention-editor-section">
            <h3 className="form-section-title">{t("adminCustomFieldsTitle")}</h3>
            {customSource.status === "error" && <p role="alert">{customSource.error.message}</p>}
            {editableCustomFields.map((field) => (
              <div
                className={["form-field", "custom-field", customFieldClassName(field)]
                  .filter(Boolean)
                  .join(" ")}
                key={field.id}
              >
                <label htmlFor={`custom-${field.id}`}>
                  {customFieldLabel(field, i18n.language, data.settings.dictionary)}
                </label>
                {field.field_type === "textarea" ? (
                  <textarea id={`custom-${field.id}`} value={customValues[field.id] ?? ""}
                    onChange={(event) => setCustomValues((prev) => ({ ...prev, [field.id]: event.target.value }))} />
                ) : (
                  <input id={`custom-${field.id}`} value={customValues[field.id] ?? ""}
                    onChange={(event) => setCustomValues((prev) => ({ ...prev, [field.id]: event.target.value }))} />
                )}
              </div>
            ))}
          </section>
        )}
        {canClassify(user) && (
          <section
            className="intervention-editor-section"
            aria-labelledby="intervention-editor-classification"
          >
            <h3 className="form-section-title" id="intervention-editor-classification">
              {t("classificationHeading")}
            </h3>
            <div className="form-grid">
              {getClassificationViews(views).map((view) => (
                <div key={view.id} className="form-field">
                  <label htmlFor={`form-view-${view.id}`}>{i18n.viewLabel(view)}</label>
                  <select
                    id={`form-view-${view.id}`}
                    value={draft.classifications[view.column] ?? ""}
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        classifications: {
                          ...current.classifications,
                          [view.column]: event.target.value,
                        },
                      }))
                    }
                  >
                    <option value="">{t("ikkeAngitt")}</option>
                    {view.order.map((code) => (
                      <option key={code} value={code}>
                        {i18n.viewValueLabel(view, code)}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </section>
        )}
        {canClassify(user) && (
          <section
            className={`intervention-editor-section working-comments${comment.trim() ? " has-comment" : ""}`}
            aria-labelledby="intervention-editor-comments"
          >
            <h3 className="form-section-title" id="intervention-editor-comments">
              {t("workingCommentsHeading")}
            </h3>
            <p className="working-comments-hint" id="intervention-editor-comments-hint">
              {t("workingCommentsHint")}
            </p>
            <textarea
              aria-labelledby="intervention-editor-comments"
              aria-describedby="intervention-editor-comments-hint"
              maxLength={10000}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
          </section>
        )}
    </>
  );

  const privateForm = (
    <>
      {!isCreate && isRespondent && feedbackEnabled && feedback.length > 0 && (
        <InterventionFeedbackPanel
          feedback={feedback}
          canAcknowledge={item.submittedBy === user?.id}
        />
      )}
      <section
        className="intervention-editor-section intervention-editor-location"
        aria-label={t("location")}
      >
        {locationField}
      </section>
      {config ? (
        <QuestionnaireForm config={config} answers={answers} onChange={setAnswer} />
      ) : pinned ? (
        <p>{t("formUnknownVersion")}</p>
      ) : (
        locationLocked && <p>{t("formChooseLocation")}</p>
      )}
    </>
  );

  return (
    <div className="intervention-editor">
      <header className="intervention-editor-header">
        <div className="details-top-row">
          <div className="details-title-row">
            {!isCreate && <span className="item details-id-badge">{item.itemNumber}</span>}
            <h2 className="intervention-editor-heading">
              {isCreate ? t("newInterventionBadge") : t("interventionEditorTitle")}
            </h2>
          </div>
          <div className="details-header-actions">
            <button
              type="button"
              className="details-close-btn"
              aria-label={t("closeDetails")}
              onClick={onClose}
            >
              ×
            </button>
          </div>
        </div>
        <p className="intervention-editor-introduction">{t("interventionEditorIntroduction")}</p>
      </header>

      <div className="intervention-editor-content">
        <InterventionTabs
          publicContent={publicFields}
          privateContent={privateForm}
          privateFirst={isRespondent}
          onTabChange={setActiveSection}
          hidePublic={hidePublicTab}
          aside={
            !isCreate &&
            item.status && <span className={`status-badge ${item.status}`}>{statusLabel}</span>
          }
        />

        {!isCreate && <UpdatedMeta item={item} />}
        {!isCreate && !isRespondent && feedbackEnabled && feedback.length > 0 && (
          <InterventionFeedbackPanel feedback={feedback} canAcknowledge={false} />
        )}
      </div>

      <footer className="intervention-editor-footer">
        <div className="details-edit-actions">
          {!isCreate && isSuperadmin(user) && canDeleteIntervention(user, item) && (
            <button
              type="button"
              className="details-delete-btn"
              disabled={busy}
              onClick={() =>
                window.confirm(t("deleteConfirm")) &&
                void runAction(
                  () => softDeleteIntervention(item.dbId!, true),
                  "editDeleteError",
                  "list",
                )
              }
            >
              {t("deleteButton")}
            </button>
          )}
          {!isCreate && canApproveIntervention(user, item) && item.status !== "approved" && (
            <button
              type="button"
              className="details-approve-btn"
              disabled={busy}
              onClick={() =>
                void runAction(
                  () => setInterventionStatus(item.dbId!, "approved"),
                  "editApproveError",
                  "reopen",
                )
              }
            >
              {t("approveButton")}
            </button>
          )}
          {!isCreate && canReturnToDraft(user, item) && (
            <button
              type="button"
              className="details-return-btn"
              disabled={busy}
              onClick={() => {
                if (!feedbackEnabled) {
                  const confirmation = item.status === "approved"
                    ? "returnToDraftPublishedConfirm" : "returnToDraftConfirm";
                  if (window.confirm(t(confirmation))) {
                    void runAction(
                      () => setInterventionStatus(item.dbId!, "draft"),
                      "editReturnError", "reopen",
                    );
                  }
                  return;
                }
                setReturnComment("");
                setShowReturnDialog(true);
              }}
            >
              {t("returnButton")}
            </button>
          )}
          <button type="button" className="intervention-editor-cancel" onClick={onClose}>
            {t("systemCreateAnalysisCancel")}
          </button>
          <button
            type="button"
            className="details-save-btn"
            disabled={busy}
            onClick={() => void save(isRespondent ? "draft" : null)}
          >
            {isRespondent ? t("saveDraftButton") : t("saveButton")}
          </button>
          {isRespondent && (
            <button
              type="button"
              className="details-approve-btn details-submit-btn"
              disabled={busy}
              onClick={() => void save("submitted")}
            >
              {t("submitButton")}
            </button>
          )}
        </div>
        <StatusMessage message={message} />
      </footer>
      {feedbackEnabled && showReturnDialog && !isCreate && canReturnToDraft(user, item) && (
        <div className="intervention-feedback-overlay">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="return-feedback-title"
            aria-describedby="return-feedback-hint"
            className="intervention-feedback-dialog"
          >
            <h3 id="return-feedback-title">{t("feedbackReturnDialogTitle")}</h3>
            <p id="return-feedback-hint">
              {t(
                item.status === "approved"
                  ? "returnToDraftPublishedConfirm"
                  : "returnToDraftConfirm",
              )}
            </p>
            <div className="form-field">
              <label htmlFor="return-feedback-message">{t("feedbackCommentLabel")}</label>
              <textarea
                id="return-feedback-message"
                rows={4}
                maxLength={1000}
                disabled={!item.submittedBy || busy}
                value={returnComment}
                onChange={(event) => setReturnComment(event.target.value)}
                placeholder={t("feedbackCommentPlaceholder")}
              />
              <p className="intervention-feedback-meta">
                {item.submittedBy ? t("feedbackCommentHint") : t("feedbackRecipientMissing")}
                {item.submittedBy && ` · ${returnComment.length}/1000`}
              </p>
            </div>
            <div className="intervention-feedback-actions">
              <button
                type="button"
                className="intervention-editor-cancel"
                disabled={busy}
                onClick={() => setShowReturnDialog(false)}
              >
                {t("systemCreateAnalysisCancel")}
              </button>
              <button
                type="button"
                className="details-save-btn"
                disabled={busy}
                onClick={() =>
                  void runAction(
                    () => returnToDraftWithFeedback(item.dbId!, item.submittedBy ? returnComment : ""),
                    "editReturnError",
                    "reopen",
                  )
                }
              >
                {t("returnButton")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  type?: "text" | "number";
  multiline?: boolean;
  required?: boolean;
  onChange(value: string): void;
}

function TextField({
  id,
  label,
  value,
  type = "text",
  multiline = false,
  required = false,
  onChange,
}: TextFieldProps) {
  return (
    <div className="form-field">
      <label htmlFor={id}>{label}</label>
      {multiline ? (
        <textarea id={id} value={value} onChange={(event) => onChange(event.target.value)} />
      ) : (
        <input
          type={type}
          id={id}
          required={required}
          min={type === "number" ? 0 : undefined}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </div>
  );
}
